import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  DeleteUserParams,
  DeleteUserData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface DeleteUserState
  extends MergeRequestState<DeleteUserData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.deleteUser,
  isFetching: (state: ReduxState) => state.server.deleteUser.isFetching,
  data: (state: ReduxState) => state.server.deleteUser.data,
  error: (state: ReduxState) => state.server.deleteUser.error,
};

const initialState: DeleteUserState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/deleteUser';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    { params, config }: { params: DeleteUserParams; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.deleteUser(params, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerDeleteUser {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const deleteUser: ServerDeleteUser = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
